'use client';
import React from 'react';
import { CheckCircle } from 'lucide-react';

type Step = { id: string; label: string; short: string };

interface Props {
  steps: Step[];
  currentStep: number;
  completedSteps: number;
  onStepClick: (index: number) => void;
}

export default function StepIndicator({ steps, currentStep, completedSteps, onStepClick }: Props) {
  return (
    <div className="w-full overflow-x-auto scrollbar-thin pb-1 -mx-1 px-1">
      <div className="flex items-center min-w-max gap-0">
        {steps.map((step, i) => {
          const isCompleted = i < completedSteps;
          const isCurrent = i === currentStep;
          const isClickable = i < completedSteps;

          return (
            <React.Fragment key={`step-indicator-${step.id}`}>
              <button
                onClick={() => isClickable && onStepClick(i)}
                disabled={!isClickable}
                className={`flex items-center gap-2 px-2.5 sm:px-3 py-2 rounded-xl transition-all duration-200 min-h-[44px] touch-manipulation ${
                  isCurrent
                    ? 'bg-brand-600/20 border border-brand-500/40'
                    : isCompleted
                    ? 'hover:bg-white/5 cursor-pointer' :'cursor-default opacity-50'
                }`}
              >
                <div
                  className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold shrink-0 transition-all ${
                    isCompleted
                      ? 'bg-green-500 text-white'
                      : isCurrent
                      ? 'bg-gradient-primary text-white shadow-glow-sm'
                      : 'bg-navy-600 border border-white/15 text-slate-500'
                  }`}
                >
                  {isCompleted ? <CheckCircle size={14} /> : i + 1}
                </div>
                <span
                  className={`text-xs font-medium hidden sm:block ${
                    isCurrent ? 'text-brand-300' : isCompleted ? 'text-slate-400' : 'text-slate-600'
                  }`}
                >
                  {step.short}
                </span>
              </button>
              {i < steps.length - 1 && (
                <div className={`h-px w-4 sm:w-6 mx-0.5 sm:mx-1 shrink-0 transition-colors ${i < completedSteps ? 'bg-green-500/40' : 'bg-white/10'}`} />
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}